import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { map, filter, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  HeaderData,
  HeaderIconAction,
  HeaderService,
  HeaderVariant,
} from './header.service';
import { GlobalState } from '../../store/store.model';
import { selectRouteData } from '../../store/router.selectors';

type HeaderVm = Readonly<{
  visible: boolean;
  variant: HeaderVariant;
  title: string;
  isRaw: boolean;
  actions: HeaderIconAction[];
}>;

type RouteHeaderData = {
  hideHeader?: boolean;
  tab?: string;
  headerVariant?: HeaderVariant;
  headerTitle?: string;
  headerTitleRaw?: boolean;
};

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TranslocoModule, MatIconModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly store = inject(Store<GlobalState>);
  private readonly router = inject(Router);
  private readonly headerService = inject(HeaderService);

  private readonly routeData = toSignal(this.store.select(selectRouteData), {
    initialValue: null,
  });

  private readonly headerState = toSignal(this.headerService.headerData$, {
    initialValue: this.headerService.snapshot,
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects || event.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  public readonly vm = computed<HeaderVm>(() => {
    const data = (this.routeData() ?? {}) as RouteHeaderData;
    const state = (this.headerState() ?? {
      text: '',
      showMenu: false,
      menuItems: [],
      iconActions: [],
    }) as HeaderData;
    const currentUrl = this.normalizeUrl(this.currentUrl());

    if (data.hideHeader) {
      return {
        visible: false,
        variant: 'back',
        title: '',
        isRaw: false,
        actions: [],
      };
    }

    const variant = this.resolveVariant(data, state, currentUrl);
    const title = this.resolveTitle(data, state);
    const isRaw = !!state.isRawText || !!data.headerTitleRaw;
    const actions = variant === 'brand' ? [] : state.iconActions ?? [];

    return {
      visible: true,
      variant,
      title,
      isRaw,
      actions,
    };
  });

  public onBackClick(): void {
    void this.headerService.navigateBack(this.currentUrl());
  }

  public onCloseClick(): void {
    void this.headerService.navigateBack(this.currentUrl());
  }

  public onNotificationClick(): void {
    void this.router.navigate(['/tabs/notification']);
  }

  public onActionClick(action: HeaderIconAction): void {
    action.customAction?.();
  }

  private resolveVariant(
    data: RouteHeaderData,
    state: HeaderData,
    currentUrl: string,
  ): HeaderVariant {
    if (state.variant) {
      return state.variant;
    }

    if (data.headerVariant) {
      return data.headerVariant;
    }

    if (this.isPrimaryTabRoute(data, currentUrl)) {
      return 'brand';
    }

    if (currentUrl.startsWith('/tabs/collection-item')) {
      return 'close';
    }

    return 'back';
  }

  private resolveTitle(data: RouteHeaderData, state: HeaderData): string {
    if (state.text) {
      return state.text;
    }

    if (data.headerTitle) {
      return data.headerTitle;
    }

    return '';
  }

  private isPrimaryTabRoute(data: RouteHeaderData, currentUrl: string): boolean {
    if (data.headerVariant === 'brand') {
      return true;
    }

    return (
      currentUrl === '/tabs/news' ||
      currentUrl === '/tabs/collection' ||
      currentUrl === '/tabs/notification' ||
      currentUrl === '/tabs/profile' ||
      currentUrl === '/tabs/guest'
    );
  }

  private normalizeUrl(url: string): string {
    if (!url) {
      return '';
    }

    const [path] = url.split('?');
    const [normalizedPath] = path.split('#');
    return normalizedPath || '';
  }
}
